import { APP_WILL_MOUNT } from '../base/app/actionTypes';
import { CONFERENCE_JOINED } from '../base/conference/actionTypes';
import { getCurrentConference } from '../base/conference/functions';
import { pinParticipant } from '../base/participants/actions';
import { DOMINANT_SPEAKER_CHANGED, PARTICIPANT_JOINED, PARTICIPANT_LEFT, PARTICIPANT_UPDATED } from '../base/participants/actionTypes';
import { getLocalScreenShareParticipant, getPinnedParticipant, getRemoteParticipants } from '../base/participants/functions';
import MiddlewareRegistry from '../base/redux/MiddlewareRegistry';
import { setChatHistoryVisible } from '../chat_history/actions';
import { addStageParticipant, removeStageParticipant } from '../filmstrip/actions.web';
import { isStageFilmstripEnabled } from '../filmstrip/functions.web';
import { setTileView } from '../video-layout/actions.any';
import { SET_TILE_VIEW } from '../video-layout/actionTypes';
import { isLayoutTileView } from '../video-layout/functions.any';
import { setDeepaiTheme } from './actions';
import { SET_DEEPAI_THEME } from './actionTypes';
import { applyThemeToDom, getDeepaiTheme, getThemeFromUrl, normalizeTheme } from './functions';
/**
 * Re-entrancy guard. Dispatching pinParticipant() synchronously re-enters this
 * middleware (via the large-video state listeners), so without this flag
 * ensureAiOnStage() would recurse until the call stack blows up.
 */
let aiPinInFlight = false;
/**
 * The AI id we ourselves last pinned (or undefined once released). We can't rely
 * on getPinnedParticipant() to confirm our own pin, because under the stage
 * filmstrip it reads from features/filmstrip.activeParticipants rather than the
 * pinnedParticipant we set — so it would never reflect our pin and we'd re-pin
 * forever. Tracking our own request makes the "already pinned" check reliable.
 */
let ourPinnedAiId;
/**
 * Finds the AI participant (the one that advertised deepaiRole=ai).
 *
 * @param {Object} state - The redux state.
 * @returns {Object|undefined}
 */
function findAiParticipant(state) {
    for (const p of getRemoteParticipants(state).values()) {
        if (p.deepaiRole === 'ai') {
            return p;
        }
    }
    return undefined;
}
/**
 * Dispatches a pin change guarded against synchronous re-entry.
 *
 * @param {IStore} store - The redux store.
 * @param {string|null} id - Participant id to pin, or null to release.
 * @returns {void}
 */
function pinGuarded(store, id) {
    const prev = ourPinnedAiId;
    ourPinnedAiId = id ?? undefined;
    aiPinInFlight = true;
    try {
        // Plain pin (used by _electParticipantInLargeVideo when the stage
        // filmstrip is OFF).
        store.dispatch(pinParticipant(id));
        // Under the stage filmstrip, _electParticipantInLargeVideo reads the pin
        // from filmstrip.activeParticipants — NOT pinnedParticipant — so a plain
        // pin is invisible to the election and the AI gets bumped by the
        // dominant speaker whenever another participant talks. Register the AI as
        // a *pinned stage participant* too so the election keeps it on stage.
        if (isStageFilmstripEnabled(store.getState())) {
            if (id) {
                store.dispatch(addStageParticipant(id, true));
            }
            else if (prev) {
                store.dispatch(removeStageParticipant(prev));
            }
        }
    }
    finally {
        aiPinInFlight = false;
    }
}
/**
 * Keeps the AI participant pinned to the large video (stage) so it never gets
 * bumped by dominant-speaker / newest-participant election. Written to respect
 * the other display formats:
 *   - only in a themed layout (default look is untouched),
 *   - never forces stage while the user is in tile (grid) view,
 *   - yields the stage to an active screen share (releases the AI pin),
 *   - respects a manual pin of some other participant (only auto-pins when
 *     nothing is pinned or the AI is already pinned).
 *
 * @param {IStore} store - The redux store.
 * @returns {void}
 */
function ensureAiOnStage(store) {
    // Our own pinParticipant() dispatch re-enters this middleware synchronously;
    // bail out so we never recurse.
    if (aiPinInFlight) {
        return;
    }
    const state = store.getState();
    const ai = findAiParticipant(state);
    const pinned = getPinnedParticipant(state);
    // Default look / theme off: release an AI pin we set, else leave.
    if (!getDeepaiTheme(state)) {
        if (ourPinnedAiId) {
            pinGuarded(store, null);
        }
        return;
    }
    // Grid view: don't force a stage participant.
    if (isLayoutTileView(state)) {
        return;
    }
    // A screen share should own the stage — release the AI pin if we set one.
    const screenshareActive = Boolean(state['features/video-layout'].remoteScreenShares?.length)
        || Boolean(getLocalScreenShareParticipant(state));
    if (screenshareActive) {
        if (ourPinnedAiId) {
            pinGuarded(store, null);
        }
        return;
    }
    // AI left: forget our pin so we re-pin cleanly if it returns.
    if (!ai) {
        if (ourPinnedAiId) {
            ourPinnedAiId = undefined;
        }
        return;
    }
    // The AI is already the elected stage participant → nothing to do. Now that
    // pinGuarded also registers the AI as a pinned stage participant,
    // getPinnedParticipant reliably reflects it under BOTH the stage filmstrip
    // and the plain layout, so this check self-heals: if the AI ever gets bumped
    // off stage, we fall through and re-pin.
    if (pinned?.id === ai.id) {
        return;
    }
    // Respect a manual pin of another (non-AI) participant.
    if (pinned && pinned.id !== ai.id) {
        return;
    }
    pinGuarded(store, ai.id);
}
/**
 * Applies the layout side-effects that the themed conference composition needs:
 * stage view (speaker front-and-center) and the always-on 会話履歴 right column.
 *
 * @param {IStore} store - The redux store.
 * @param {string} theme - The active theme id (empty = default look).
 * @returns {void}
 */
/**
 * Whether the viewport is a landscape phone (short height). There the chat
 * history is a right-side overlay covering the stage, so we don't auto-open it.
 * Portrait phones embed the chat below the AI card, so auto-open is fine there.
 *
 * @returns {boolean}
 */
function isLandscapePhone() {
    if (typeof window === 'undefined') {
        return false;
    }
    return window.innerHeight <= 540;
}
function applyThemeLayout(store, theme) {
    // Stage view puts the speaker large; the chat-history panel becomes the
    // right column. Both are reversible by the user.
    store.dispatch(setTileView(!theme));
    // 会話履歴は会議参加後のみ出す。待機ルーム(プレジョイン)では出さない。
    // 横スマホは右サイドが映像を覆うので自動オープンしない(縦は下に埋め込むのでOK)。
    const inConference = Boolean(getCurrentConference(store.getState()));
    store.dispatch(setChatHistoryVisible(Boolean(theme) && inConference && !isLandscapePhone()));
}
/**
 * Nudges Jitsi to recompute the large-video / filmstrip sizes against the
 * themed containers. Needed because those sizes are computed in JS on resize,
 * and the themed layout changes the container geometry.
 *
 * @returns {void}
 */
function nudgeLayoutRecalc() {
    if (typeof window === 'undefined') {
        return;
    }
    // Fire a few delayed resizes so it also catches the moment the large video
    // element is attached shortly after join.
    [300, 900, 1800].forEach(ms => {
        setTimeout(() => window.dispatchEvent(new Event('resize')), ms);
    });
}
/**
 * Middleware that keeps the DOM `data-theme` attribute in sync with the
 * persisted DeepAI theme selection.
 *
 * @param {IStore} store - The redux store.
 * @returns {Function}
 */
MiddlewareRegistry.register(store => next => action => {
    switch (action.type) {
        case APP_WILL_MOUNT: {
            // A `?deepaiTheme=<id>` URL param overrides the persisted selection
            // (use `?deepaiTheme=none` for the plain look). Handy for A/B testing
            // themes / perf without going through the settings UI.
            const urlTheme = getThemeFromUrl();
            if (urlTheme !== undefined) {
                // Seeds redux + persistence; the SET_DEEPAI_THEME case below applies
                // it to the DOM and layout.
                store.dispatch(setDeepaiTheme(urlTheme));
                break;
            }
            // Apply the persisted theme as early as possible on startup.
            const persisted = getDeepaiTheme(store.getState());
            applyThemeToDom(persisted);
            if (persisted) {
                applyThemeLayout(store, persisted);
            }
            break;
        }
        case CONFERENCE_JOINED: {
            // 参加完了。映像要素が揃うのでステージ表示＋会話履歴を開き、サイズ再計算を促す。
            // getCurrentConference はこの時点でまだ未反映のことがあるため、参加時は明示的に開く。
            const theme = getDeepaiTheme(store.getState());
            if (theme) {
                store.dispatch(setTileView(false));
                store.dispatch(setChatHistoryVisible(!isLandscapePhone()));
                nudgeLayoutRecalc();
            }
            break;
        }
        case SET_DEEPAI_THEME: {
            // Normalize so 'none' maps to the plain look ('') rather than a bogus
            // data-theme="none".
            const eff = normalizeTheme(action.theme);
            applyThemeToDom(eff);
            applyThemeLayout(store, eff);
            nudgeLayoutRecalc();
            break;
        }
    }
    const result = next(action);
    // Re-evaluate which participant owns the stage AFTER the action updated the
    // store (new participant / dominant speaker / layout / theme change).
    switch (action.type) {
        case CONFERENCE_JOINED:
        case DOMINANT_SPEAKER_CHANGED:
        case PARTICIPANT_JOINED:
        case PARTICIPANT_UPDATED:
        case PARTICIPANT_LEFT:
        case SET_TILE_VIEW:
        case SET_DEEPAI_THEME:
            ensureAiOnStage(store);
            break;
    }
    return result;
});
